import { defineComponent, h } from 'vue';
import type { DefineComponent, PropType } from 'vue';

import type { FileOptions } from '../components/File';
import { DIFFS_TAG_NAME } from '../constants';
import type {
  FileContents,
  LineAnnotation,
  SelectedLineRange,
  VirtualFileMetrics,
} from '../types';
import { useFileInstance } from './composables/useFileInstance';
import type { FilePropsVue } from './types';
import { renderFileChildren } from './utils/renderFileChildren';
import { templateRender } from './utils/templateRender';

// Renders a single code file (no diff). Custom UI is provided via the `#header`,
// `#headerPrefix`, `#headerMetadata`, `#annotation`, and `#gutter` scoped slots
// (see `FileSlotsVue`).
const FileImpl = defineComponent({
  name: 'PierreFile',
  props: {
    file: { type: Object as PropType<FileContents>, required: true },
    options: {
      type: Object as PropType<FileOptions<undefined>>,
      default: undefined,
    },
    metrics: {
      type: Object as PropType<VirtualFileMetrics>,
      default: undefined,
    },
    lineAnnotations: {
      type: Array as PropType<LineAnnotation[]>,
      default: undefined,
    },
    selectedLines: {
      type: Object as PropType<SelectedLineRange | null>,
      default: undefined,
    },
    prerenderedHTML: { type: String, default: undefined },
    disableWorkerPool: { type: Boolean, default: false },
  },
  setup(props, { slots }) {
    const { containerRef, getHoveredLine } = useFileInstance({
      props,
      hasCustomHeader: () => slots.header != null,
      hasGutterRenderer: () => slots.gutter != null,
    });
    return () =>
      h(
        DIFFS_TAG_NAME,
        { ref: containerRef },
        templateRender(
          renderFileChildren({
            file: props.file,
            slots,
            lineAnnotations: props.lineAnnotations,
            getHoveredLine,
          }),
          props.prerenderedHTML
        )
      );
  },
});

// See the note on `FileDiff` for why this explicit annotation/cast is required.
export const File = FileImpl as unknown as DefineComponent<FilePropsVue>;
